import {Box} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {GridColDef} from '@mui/x-data-grid';
import {getIntercomDeviceAuditLogs} from 'src/apis/autoGenerate/services';
import {IntercomDeviceLog} from 'src/apis/autoGenerate/types/intercomDeviceLog';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {useOrgId} from 'src/hooks';
import {useAppSelector} from 'src/redux/hooks';
import {selectProperty} from 'src/redux/slice/propertySlice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import placeholderImg from '../../../assets/placeholder_call_event.png';
import EventDetails from 'src/components/EventDetails';
import SummaryBar, {SummaryStatItem} from 'src/components/SummaryBar';
import EventFilterBar, {
  DateRangePreset,
  StatusFilter,
} from 'src/components/EventFilterBar';
import {EntioDataGrid, StatusBadge, getEventStatusVariant} from 'src/components/ui';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import {brand} from 'src/theme';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const PAGE_SIZE = 10;

function getRelativeTime(dateTime: string, deviceTimezone?: string) {
  if (!dateTime) return '-';
  try {
    const tz = deviceTimezone || 'UTC';
    const utcTime = dayjs.utc(dateTime);
    const now = dayjs();
    const diffMinutes = now.diff(utcTime, 'minute');
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = now.diff(utcTime, 'hour');
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = now.diff(utcTime, 'day');
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return dayjs.utc(dateTime).tz(tz).format('MMM DD, YYYY');
  } catch {
    return dayjs.utc(dateTime).format('MMM DD, YYYY');
  }
}

export default function CallEvent() {
  const [searchApartment, setSearchApartment] = useState<any>(null);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('30d');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [allLogs, setAllLogs] = useState<IntercomDeviceLog[]>([]);
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: PAGE_SIZE});
  const [selectedEvent, setSelectedEvent] = useState<IntercomDeviceLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const property = useAppSelector(selectProperty);
  const {orgId} = useOrgId();

  const dateRange = useMemo(() => {
    const end = dayjs();
    switch (dateRangePreset) {
      case '7d':
        return {start: end.subtract(7, 'day'), end};
      case '30d':
        return {start: end.subtract(30, 'day'), end};
      case '90d':
        return {start: end.subtract(90, 'day'), end};
      case 'all':
      default:
        return {start: null, end: null};
    }
  }, [dateRangePreset]);

  const fetchLogs = async () => {
    try {
      if (!orgId) {
        setLoading(false);
        return;
      }
      if (property.loadingList) {
        setLoading(false);
        return;
      }

      setLoading(true);

      // Send full ISO timestamps so the backend filter is inclusive of "now"
      // (a YYYY-MM-DD endDate is parsed as 00:00 UTC on the server, which
      // would exclude events that happened later in the day).
      const startDate = dateRange.start?.toISOString();
      const endDate = dateRange.end?.toISOString();

      const res = await getIntercomDeviceAuditLogs(
        orgId,
        'INTERCOM_CALL',
        undefined,
        startDate,
        endDate,
        '1',
        '1000',
        undefined,
        searchApartment?.id
      );
      const logs = res.data.data.logs || [];
      logs.sort(
        (a: IntercomDeviceLog, b: IntercomDeviceLog) =>
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );
      setAllLogs(logs);
    } catch (error) {
      console.error('Error fetching call event logs:', error);
      setAllLogs([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, [orgId, property.loadingList, searchApartment, dateRange.start?.valueOf(), dateRange.end?.valueOf()]);

  const filteredLogs = useMemo(() => {
    let result = allLogs;

    if (statusFilter !== 'all') {
      result = result.filter(
        log => (log.status || '').toLowerCase() === statusFilter
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(log => {
        const userName = log.entrio_users
          ? `${log.entrio_users.first_name} ${log.entrio_users.last_name}`.toLowerCase()
          : '';
        const deviceName = (log.device?.name || '').toLowerCase();
        return userName.includes(q) || deviceName.includes(q);
      });
    }

    return result;
  }, [allLogs, statusFilter, searchQuery]);

  const paginatedLogs = useMemo(() => {
    const startIdx = paginationModel.page * paginationModel.pageSize;
    return filteredLogs.slice(startIdx, startIdx + paginationModel.pageSize);
  }, [filteredLogs, paginationModel]);

  const summaryStats: SummaryStatItem[] = useMemo(() => {
    const total = filteredLogs.length;
    const success = filteredLogs.filter(l => (l.status || '').toLowerCase() === 'success').length;
    const failed = total - success;

    return [
      {label: 'Total Calls', value: total, color: 'default' as const},
      {
        label: 'Success',
        value: success,
        color: 'success' as const,
        icon: <CheckCircleOutlineIcon sx={{fontSize: 18}} />,
      },
      {
        label: 'Failed',
        value: failed,
        color: 'error' as const,
        icon: <ErrorOutlineIcon sx={{fontSize: 18}} />,
      },
    ];
  }, [filteredLogs]);

  const handleApartmentChange = (val: Apartment | null) => {
    setSearchApartment(val ?? null);
    setPaginationModel(prev => ({...prev, page: 0}));
  };

  const handleRowClick = (params: any) => {
    setSelectedEvent(params.row);
    setDetailsOpen(true);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'preview',
        headerName: 'Preview',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: params => {
          const photoUrl = params.row.photo?.photoUrl;
          const imgSrc = photoUrl || placeholderImg;
          return (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Box
                component="img"
                src={imgSrc}
                alt="preview"
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 1.5,
                  objectFit: 'cover',
                }}
              />
            </Box>
          );
        },
      },
      {
        field: 'event',
        headerName: 'Event',
        flex: 1,
        minWidth: 140,
        sortable: false,
        renderCell: () => (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, height: '100%'}}>
            <PhoneIcon sx={{fontSize: 18, color: brand[500]}} />
            <Box sx={{fontWeight: 500, fontSize: '0.875rem'}}>Call Event</Box>
          </Box>
        ),
      },
      {
        field: 'callingTo',
        headerName: 'User',
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueGetter: params =>
          params.row.entrio_users
            ? `${params.row.entrio_users.first_name} ${params.row.entrio_users.last_name}`
            : '-',
      },
      {
        field: 'callType',
        headerName: 'Method',
        flex: 0.8,
        minWidth: 130,
        sortable: false,
        valueGetter: params => {
          const callType = params.row.metadata?.callType || params.row.metadata?.call_type || '-';
          if (params.row.metadata?.source === 'FRONT_DESK' || callType === 'FRONT_DESK') {
            return 'Front Desk';
          }
          if (callType === 'TENANT_CALL') return 'Call Resident';
          if (callType === 'MANAGER_CALL') return 'Call Manager';
          return callType;
        },
      },
      {
        field: 'device',
        headerName: 'Device',
        flex: 0.9,
        minWidth: 120,
        sortable: false,
        valueGetter: params => params.row.device?.name || '-',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: params => {
          const s = (params.row.status || '').toLowerCase();
          return (
            <StatusBadge
              status={getEventStatusVariant(s)}
              label={s === 'success' ? 'SUCCESS' : 'FAILED'}
            />
          );
        },
      },
      {
        field: 'time',
        headerName: 'Time',
        width: 120,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const relTime = getRelativeTime(
            params.row.created_at,
            params.row.device?.timezone
          );
          return (
            <Box sx={{fontSize: '0.8rem', color: 'text.secondary'}}>
              {relTime}
            </Box>
          );
        },
      },
    ],
    []
  );

  return (
    <Box>
      {/* Filter Bar */}
      <EventFilterBar
        dateRange={dateRangePreset}
        onDateRangeChange={v => {
          setDateRangePreset(v);
          setPaginationModel(prev => ({...prev, page: 0}));
        }}
        eventType="all"
        onEventTypeChange={() => {}}
        statusFilter={statusFilter}
        onStatusFilterChange={v => {
          setStatusFilter(v);
          setPaginationModel(prev => ({...prev, page: 0}));
        }}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onApartmentChange={handleApartmentChange}
      />

      {/* Summary Bar */}
      <Box mt={2}>
        <SummaryBar stats={summaryStats} />
      </Box>

      {/* Data Grid */}
      <Box mt={2}>
        <EntioDataGrid
          loading={loading}
          rows={paginatedLogs.map((log, idx) => ({...log, id: log.id || idx}))}
          columns={columns}
          rowCount={filteredLogs.length}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={handleRowClick}
          pageSizeOptions={[5, 10, 25]}
          richRows
          emptyMessage="No call events"
          emptyDescription="Call events will appear here once your devices are connected."
        />
      </Box>

      <EventDetails
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        eventType="call"
      />
    </Box>
  );
}
